import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SearchCard } from "../components/SearchCard";
import { ProposalCard } from "../components/ProposalCard";
import { SectionTitle, EmptyState, Loader, Card } from "../components/ui-bits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PlusCircle, Search, Heart, Download, Radio } from "lucide-react";

export default function ArtistDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [searches, setSearches] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    api.get("/searches/mine").then((r) => setSearches(r.data));
    api.get("/artist/favorites").then((r) => setFavorites(r.data));
    api.get("/artist/downloads").then((r) => setDownloads(r.data));
  }, []);

  if (searches === null) return <Loader />;
  const active = searches.filter((s) => s.status === "active");
  const totalProps = searches.reduce((a, s) => a + (s.submission_count || 0), 0);

  const stats = [
    { label: "Recherches", value: searches.length, icon: Search },
    { label: "Propositions", value: totalProps, icon: Radio },
    { label: "Favoris", value: favorites.length, icon: Heart },
    { label: "Téléchargements", value: downloads.length, icon: Download },
  ];

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold">Salut @{user?.pseudo} 👋</h1>
          <p className="text-sm text-zinc-400 mt-1">Gère tes recherches et écoute les prods reçues.</p>
        </div>
        <button onClick={() => nav("/app/create")} data-testid="nav-create-search-button"
          className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold text-sm glow-violet transition-all">
          <PlusCircle size={17} /> Nouvelle recherche
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="p-4">
              <Icon size={18} className="text-violet-400 mb-2" />
              <p className="text-2xl font-display font-bold">{s.value}</p>
              <p className="text-xs text-zinc-400">{s.label}</p>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="searches">
        <TabsList className="bg-zinc-900 border border-zinc-800 mb-5">
          <TabsTrigger value="searches" data-testid="tab-searches">Mes recherches</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">Favoris</TabsTrigger>
          <TabsTrigger value="downloads" data-testid="tab-downloads">Téléchargements</TabsTrigger>
        </TabsList>

        <TabsContent value="searches">
          {searches.length === 0 ? (
            <EmptyState icon={Search} title="Aucune recherche" desc="Crée ta première recherche pour recevoir des prods."
              action={<button onClick={() => nav("/app/create")} className="px-5 py-2.5 rounded-full bg-violet-600 hover:bg-violet-500 font-medium text-sm transition-colors">Nouvelle recherche</button>} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searches.map((s) => <SearchCard key={s.id} search={s} ctaLabel="Voir les propositions" />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites">
          {favorites.length === 0 ? (
            <EmptyState icon={Heart} title="Aucun favori" desc="Réagis ❤️ ou 🔥 aux prods pour les retrouver ici." />
          ) : (
            <div className="space-y-3">{favorites.map((f) => <ProposalCard key={f.submission_id} item={f} searchId={f.search_id}
              onReact={(id, t) => setFavorites((p) => (t === "love" || t === "fire") ? p.map((x) => x.submission_id === id ? { ...x, reaction: t } : x) : p.filter((x) => x.submission_id !== id))} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="downloads">
          {downloads.length === 0 ? (
            <EmptyState icon={Download} title="Aucun téléchargement" desc="Les MP3 que tu télécharges apparaîtront ici." />
          ) : (
            <div className="space-y-3">{downloads.map((d) => <ProposalCard key={d.submission_id} item={d} searchId={d.search_id} showReactions={false} />)}</div>
          )}
        </TabsContent>
      </Tabs>

      <button onClick={() => nav("/app/create")} className="sm:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 flex items-center justify-center glow-violet" data-testid="fab-create-search">
        <PlusCircle size={26} />
      </button>
    </div>
  );
}
